import { useCallback, useEffect, useState } from 'react';

import { THEME_CLASSES, isDarkTheme, isThemeId, themeById, type ThemeId } from '@/config/themes';

const STORAGE_KEY = 'sipaud-theme';

function readInitialTheme(): ThemeId {
  const stored = localStorage.getItem(STORAGE_KEY);

  if (isThemeId(stored)) {
    return stored;
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Mengelola tema tampilan.
 *
 * Nilai awal dibaca dari localStorage — skrip kecil di index.html sudah
 * memasang classnya sebelum React ter-mount, jadi hook ini hanya perlu
 * menjaga agar state React tetap sinkron dengan DOM.
 *
 * Pilihan tema disimpan per peramban, bukan per akun: ia preferensi tampilan
 * pribadi, dan menyimpannya di server berarti satu perjalanan jaringan lagi
 * sebelum halaman boleh digambar — persis yang membuat kedipan putih yang
 * skrip di index.html hindari.
 */
export function useTheme() {
  const [theme, setTheme] = useState<ThemeId>(readInitialTheme);

  useEffect(() => {
    const root = document.documentElement;

    // Dilepas semua lebih dulu: berpindah dari satu tema gelap ke tema gelap
    // lain akan menumpuk dua class sekaligus, dan yang menang jadi urusan
    // urutan penulisan di CSS — bukan pilihan pemakainya.
    root.classList.remove(...THEME_CLASSES);

    const className = themeById(theme).className;
    if (className) {
      root.classList.add(className);
    }

    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Untuk layar yang hanya mengenal dua keadaan — Portal Wali Murid. Beralih
  // dari tema neon karena itu jatuh ke Terang, bukan berputar ke tema neon
  // lain yang tidak pernah ia tampilkan namanya.
  const toggleTheme = useCallback(() => {
    setTheme((current) => (isDarkTheme(current) ? 'light' : 'dark'));
  }, []);

  return { theme, isDark: isDarkTheme(theme), setTheme, toggleTheme };
}
